import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, CITIES, CUISINES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Truck, MapPin, Plus, Trash2, Crown, Radio, CreditCard, CalendarDays, XCircle, Download, Mail, Receipt, DollarSign, Clock, Sparkles, Loader2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [truck, setTruck] = useState(undefined); // undefined=loading, null=none
  const [apps, setApps] = useState([]);
  const [leads, setLeads] = useState(null);

  const loadTruck = () => api.get("/my/truck").then((r) => setTruck(r.data || null)).catch(() => setTruck(null));
  useEffect(() => { loadTruck(); api.get("/my/applications").then((r) => setApps(r.data)).catch(() => {}); }, []);

  const loadLeads = () => api.get("/corporate-bookings").then((r) => setLeads(r.data)).catch((e) => setLeads({ error: apiError(e) }));

  if (truck === undefined) return <Spinner />;

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Owner Dashboard</h1>
          <p className="text-slate-500">Hi {user.name} — manage your truck, location & bookings.</p>
        </div>
        {truck && <TierBadge tier={truck.tier} />}
      </div>

      {!truck ? (
        <TruckForm onSaved={loadTruck} />
      ) : (
        <Tabs defaultValue="truck" className="mt-8">
          <TabsList className="bg-white border border-teal-100 rounded-full p-1">
            <TabsTrigger value="truck" className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-truck">My Truck</TabsTrigger>
            <TabsTrigger value="location" className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-location">Live Location</TabsTrigger>
            <TabsTrigger value="applications" className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-applications">Event Applications</TabsTrigger>
            <TabsTrigger value="leads" onClick={loadLeads} className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-leads">Corporate Leads</TabsTrigger>
            <TabsTrigger value="commissions" className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-commissions">Sales &amp; Commissions</TabsTrigger>
            <TabsTrigger value="subscription" className="rounded-full data-[state=active]:bg-[#0FB5A6] data-[state=active]:text-white" data-testid="tab-subscription">Subscription</TabsTrigger>
          </TabsList>

          <TabsContent value="truck"><TruckForm existing={truck} onSaved={loadTruck} /></TabsContent>
          <TabsContent value="location"><LocationPanel truck={truck} onSaved={loadTruck} /></TabsContent>
          <TabsContent value="applications">
            <div className="mt-6 space-y-3">
              {apps.length === 0 && <Empty label="No applications yet. Browse events to apply." to="/events" cta="Browse events" />}
              {apps.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-teal-100 p-5 flex items-center justify-between gap-4" data-testid={`app-${a.id}`}>
                  <div>
                    <p className="font-bold">{a.event_title}</p>
                    <p className="text-sm text-slate-500">{a.message || "No message"}</p>
                  </div>
                  <StatusPill status={a.status} />
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="leads">
            <div className="mt-6">
              {truck.tier !== "premium" ? (
                <UpsellPremium />
              ) : leads === null ? <Spinner /> : leads.error ? (
                <UpsellPremium msg={leads.error} />
              ) : leads.length === 0 ? <p className="text-slate-400 py-8 text-center">No corporate leads yet.</p> : (
                <div className="space-y-3">
                  {leads.map((l) => (
                    <div key={l.id} className="bg-white rounded-2xl border border-teal-100 p-5" data-testid={`lead-${l.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="font-bold">{l.company} · {l.guests} guests</p>
                        <span className="text-sm text-slate-500">{l.event_date}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{l.contact_name} · {l.email} · {l.location}</p>
                      {l.details && <p className="text-sm text-slate-600 mt-2">{l.details}</p>}
                      {l.budget && <p className="text-sm font-bold text-[#0FB5A6] mt-1">Budget: ${l.budget}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="commissions"><CommissionsPanel /></TabsContent>
          <TabsContent value="subscription"><SubscriptionPanel truck={truck} onChanged={loadTruck} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function TruckForm({ existing, onSaved }) {
  const [f, setF] = useState(existing || { name: "", cuisine: "American", city: "Green Bay", description: "", phone: "", cover_url: "", hours: "", menu: [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const addItem = () => set("menu", [...(f.menu || []), { name: "", price: 0, description: "" }]);
  const setItem = (i, k, v) => set("menu", f.menu.map((m, idx) => idx === i ? { ...m, [k]: k === "price" ? v : v } : m));
  const rmItem = (i) => set("menu", f.menu.filter((_, idx) => idx !== i));
  const [aiLoading, setAiLoading] = useState(null);
  const genBio = async () => {
    if (!f.name) { toast.error("Add your truck name first"); return; }
    setAiLoading("bio");
    try { const { data } = await api.post("/ai/menu-assistant", { kind: "bio", name: f.name, cuisine: f.cuisine, keywords: f.description }); set("description", data.text); }
    catch (e) { toast.error(apiError(e)); } finally { setAiLoading(null); }
  };
  const genItem = async (i, m) => {
    if (!m.name) { toast.error("Add the item name first"); return; }
    setAiLoading("item" + i);
    try { const { data } = await api.post("/ai/menu-assistant", { kind: "item", name: m.name, cuisine: f.cuisine, keywords: m.description }); setItem(i, "description", data.text); }
    catch (e) { toast.error(apiError(e)); } finally { setAiLoading(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, menu: (f.menu || []).map((m) => ({ ...m, price: Number(m.price) || 0 })) };
      if (existing) await api.put(`/trucks/${existing.id}`, payload);
      else await api.post("/trucks", payload);
      toast.success("Truck saved!");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-teal-100 p-7 gr-shadow space-y-4" data-testid="truck-form">
      {!existing && <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-[#0FB5A6]" /> Create your truck profile</h2>}
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Truck name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="truck-name" /></F>
        <F label="Cuisine">
          <Select value={f.cuisine} onValueChange={(v) => set("cuisine", v)}>
            <SelectTrigger data-testid="truck-cuisine"><SelectValue /></SelectTrigger>
            <SelectContent>{CUISINES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Home city">
          <Select value={f.city} onValueChange={(v) => set("city", v)}>
            <SelectTrigger data-testid="truck-city"><SelectValue /></SelectTrigger>
            <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Hours"><Input value={f.hours} onChange={(e) => set("hours", e.target.value)} placeholder="11am - 8pm" data-testid="truck-hours" /></F>
        <F label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="truck-phone" /></F>
        <F label="Cover image URL"><Input value={f.cover_url} onChange={(e) => set("cover_url", e.target.value)} placeholder="https://…" data-testid="truck-cover" /></F>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</Label>
          <Button type="button" variant="ghost" onClick={genBio} disabled={aiLoading === "bio"} className="h-7 text-xs font-bold text-[#0FB5A6]" data-testid="ai-bio-btn">{aiLoading === "bio" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Write with AI</Button>
        </div>
        <Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="truck-desc" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Menu</Label>
          <Button type="button" variant="ghost" onClick={addItem} className="text-[#0FB5A6] font-bold" data-testid="add-menu-item"><Plus className="w-4 h-4 mr-1" /> Add item</Button>
        </div>
        <div className="space-y-2 mt-2">
          {(f.menu || []).map((m, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input placeholder="Item" value={m.name} onChange={(e) => setItem(i, "name", e.target.value)} />
              <Input placeholder="Desc" value={m.description} onChange={(e) => setItem(i, "description", e.target.value)} />
              <Input placeholder="$" type="number" className="w-24" value={m.price} onChange={(e) => setItem(i, "price", e.target.value)} />
              <Button type="button" variant="ghost" onClick={() => genItem(i, m)} disabled={aiLoading === "item" + i} title="Write description with AI" data-testid={`ai-item-${i}`}>{aiLoading === "item" + i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#0FB5A6]" />}</Button>
              <Button type="button" variant="ghost" onClick={() => rmItem(i)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-full h-12 px-8 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="save-truck-btn">
        {saving ? "Saving…" : existing ? "Save changes" : "Create truck"}
      </Button>
    </div>
  );
}

function LocationPanel({ truck, onSaved }) {
  const [lat, setLat] = useState(truck.lat ?? "");
  const [lng, setLng] = useState(truck.lng ?? "");
  const [note, setNote] = useState(truck.location_note || "");
  const [live, setLive] = useState(!!truck.is_live);
  const [saving, setSaving] = useState(false);

  const useGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(5)); setLng(pos.coords.longitude.toFixed(5)); toast.success("Location captured from GPS"); },
      () => toast.error("Couldn't get GPS location. Enter it manually.")
    );
  };

  const save = async () => {
    if (lat === "" || lng === "") { toast.error("Set a latitude & longitude first"); return; }
    setSaving(true);
    try {
      await api.put(`/trucks/${truck.id}/location`, { lat: Number(lat), lng: Number(lng), is_live: live, location_note: note });
      toast.success(live ? "You're live on the map!" : "Location updated");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-teal-100 p-7 gr-shadow space-y-4" data-testid="location-panel">
      <div className="flex items-center justify-between p-4 rounded-xl bg-teal-50 border border-teal-100">
        <div className="flex items-center gap-2">
          <Radio className={`w-5 h-5 ${live ? "text-[#0FB5A6]" : "text-slate-400"}`} />
          <div>
            <p className="font-bold">{live ? "Live on the map" : "Currently offline"}</p>
            <p className="text-sm text-slate-500">Customers only see live trucks pinned on the map.</p>
          </div>
        </div>
        <Switch checked={live} onCheckedChange={setLive} data-testid="live-toggle" />
      </div>
      <Button type="button" onClick={useGPS} variant="outline" className="rounded-full font-bold border-teal-200" data-testid="use-gps-btn"><MapPin className="w-4 h-4 mr-2" /> Use my current GPS location</Button>
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Latitude"><Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="44.5133" data-testid="loc-lat" /></F>
        <F label="Longitude"><Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-88.0133" data-testid="loc-lng" /></F>
      </div>
      <F label="Location note"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Lambeau Field parking lot" data-testid="loc-note" /></F>
      <Button onClick={save} disabled={saving} className="rounded-full h-12 px-8 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="save-location-btn">
        {saving ? "Saving…" : "Update location"}
      </Button>
    </div>
  );
}

function SubscriptionPanel({ truck, onChanged }) {
  const [sub, setSub] = useState(null);
  const [billing, setBilling] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    api.get("/my/subscription").then((r) => setSub(r.data)).catch(() => setSub({ tier: truck.tier }));
    api.get("/my/billing").then((r) => setBilling(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/paypal/cancel-subscription");
      toast.success("Subscription cancelled — you're back on the Basic plan.");
      load();
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCancelling(false); }
  };

  const downloadInvoice = async (b) => {
    try {
      const res = await api.get(`/my/billing/${b.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `grubroute-invoice-${b.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download invoice"); }
  };

  const resendLast = async () => {
    try {
      const { data } = await api.post("/my/billing/resend-last");
      toast.success(`Invoice emailed to ${data.sent_to}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!sub) return <Spinner />;
  const tier = sub.tier || "basic";
  const isPaid = tier === "pro" || tier === "premium";

  return (
    <div className="mt-6 bg-white rounded-2xl border border-teal-100 p-7 gr-shadow" data-testid="subscription-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className={`w-12 h-12 rounded-full flex items-center justify-center ${tier === "premium" ? "bg-[#F2635F]" : tier === "pro" ? "bg-[#0FB5A6]" : "bg-slate-100"}`}>
            {isPaid ? <Crown className="w-6 h-6 text-white" /> : <Truck className="w-6 h-6 text-slate-500" />}
          </span>
          <div>
            <p className="font-display text-2xl font-extrabold capitalize">{tier} plan</p>
            <p className="text-sm text-slate-500">
              {isPaid ? "Active paid subscription" : "Free forever — upgrade for more perks"}
            </p>
          </div>
        </div>
        {sub.provider && (
          <span className="px-3 py-1.5 rounded-full bg-teal-50 text-[#0FB5A6] text-xs font-bold uppercase tracking-wider" data-testid="sub-provider">
            via {sub.provider}
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        {sub.subscribed_at && (
          <div className="p-4 rounded-xl bg-teal-50/50 border border-teal-100">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><CalendarDays className="w-4 h-4" /> Started</p>
            <p className="font-bold mt-1">{new Date(sub.subscribed_at).toLocaleDateString()}</p>
          </div>
        )}
        {sub.paypal_status && (
          <div className="p-4 rounded-xl bg-teal-50/50 border border-teal-100">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><CreditCard className="w-4 h-4" /> PayPal status</p>
            <p className="font-bold mt-1 capitalize">{sub.paypal_status.toLowerCase()}</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {!isPaid && (
          <Link to="/pricing"><Button className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="upgrade-cta">Upgrade my plan</Button></Link>
        )}
        {isPaid && (
          <Link to="/pricing"><Button variant="outline" className="rounded-full font-bold border-teal-200" data-testid="change-plan-cta">Change plan</Button></Link>
        )}
        {(sub.paypal_subscription_id || sub.stripe_subscription_id) && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full font-bold border-red-200 text-red-600 hover:bg-red-50" data-testid="cancel-sub-btn">
                <XCircle className="w-4 h-4 mr-2" /> Cancel subscription
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Cancel your {tier} subscription?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your truck will drop to the free Basic plan and lose {tier} perks (like priority map placement and corporate leads). You can re-subscribe anytime. We'll email you a confirmation.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="keep-sub-btn">Keep subscription</AlertDialogCancel>
                <AlertDialogAction onClick={() => cancelSub(sub.paypal_subscription_id ? "paypal" : "stripe")} disabled={cancelling} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-btn">
                  {cancelling ? "Cancelling…" : "Yes, cancel"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {isPaid && !sub.paypal_subscription_id && !sub.stripe_subscription_id && (
          <p className="text-sm text-slate-400 self-center">This subscription is managed by your payment provider.</p>
        )}
      </div>
      {billing.length > 0 && (
        <div className="mt-8" data-testid="billing-history">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Billing history</p>
            <button onClick={resendLast} data-testid="resend-invoice-btn" className="text-xs font-bold text-[#0FB5A6] hover:bg-teal-50 rounded-full px-3 py-1.5 transition-colors flex items-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Resend last invoice
            </button>
          </div>
          <div className="border border-teal-100 rounded-xl overflow-hidden">
            {billing.map((b) => (
              <div key={b.id} className="flex items-center justify-between px-4 py-3 border-b border-teal-50 last:border-0 text-sm gap-3">
                <span className="text-slate-500 w-24">{new Date(b.date).toLocaleDateString()}</span>
                <span className="font-semibold capitalize flex-1">{b.plan} · {b.provider}</span>
                <span className="font-bold text-slate-800">${Number(b.amount).toFixed(2)}</span>
                <button onClick={() => downloadInvoice(b)} data-testid={`invoice-dl-${b.id}`} className="text-[#0FB5A6] hover:bg-teal-50 rounded-full p-2 transition-colors" title="Download invoice">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommissionsPanel() {
  const [reportable, setReportable] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [form, setForm] = useState({});

  const load = () => {
    api.get("/my/reportable").then((r) => setReportable(r.data)).catch(() => {});
    api.get("/my/commissions").then((r) => setCommissions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const report = async (item) => {
    const val = Number(form[item.source_id]);
    if (!val || val <= 0) { toast.error("Enter your total sales for this event"); return; }
    try {
      await api.post("/commissions/report", { source_type: item.source_type, source_id: item.source_id, sales_amount: val });
      toast.success(`Reported. 10% commission = $${(val * 0.1).toFixed(2)}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pay = async (c) => {
    try {
      const { data } = await api.post(`/commissions/${c.id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-6 space-y-8" data-testid="commissions-panel">
      <div>
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><Receipt className="w-5 h-5 text-[#0FB5A6]" /> Report event sales</h3>
        <p className="text-sm text-slate-500">Self-report your total sales by end of the event day. FlavorFlow bills a 10% commission, due within 48 hours.</p>
        <div className="mt-4 space-y-3">
          {reportable.length === 0 && <p className="text-slate-400">No events awaiting a sales report. Approved events &amp; paid corporate bookings show up here.</p>}
          {reportable.map((item) => (
            <div key={`${item.source_type}-${item.source_id}`} className="bg-white rounded-2xl border border-teal-100 p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`reportable-${item.source_id}`}>
              <div className="flex-1">
                <p className="font-bold">{item.title}</p>
                <p className="text-sm text-slate-500">{item.city} · {item.date} · <span className="capitalize">{item.source_type}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input type="number" placeholder="Total sales" className="pl-6 w-40" value={form[item.source_id] || ""} onChange={(e) => setForm((f) => ({ ...f, [item.source_id]: e.target.value }))} data-testid={`sales-input-${item.source_id}`} />
                </div>
                <Button onClick={() => report(item)} className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid={`report-${item.source_id}`}>Report</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#F2635F]" /> Commission invoices</h3>
        <div className="mt-4 space-y-3">
          {commissions.length === 0 && <p className="text-slate-400">No commission invoices yet.</p>}
          {commissions.map((c) => {
            const due = c.status !== "paid";
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-teal-100 p-5 flex items-center justify-between gap-3" data-testid={`commission-${c.id}`}>
                <div>
                  <p className="font-bold">{c.source_title}</p>
                  <p className="text-sm text-slate-500">Sales ${Number(c.sales_amount).toFixed(2)} · 10% commission</p>
                  {due && <p className="text-xs text-amber-600 font-semibold flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" /> Due {new Date(c.due_at).toLocaleString()}</p>}
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-black text-slate-800">${Number(c.amount).toFixed(2)}</p>
                  {due ? (
                    <Button onClick={() => pay(c)} className="mt-1 rounded-full font-bold h-9 bg-[#F2635F] hover:bg-[#e0554f] text-white" data-testid={`pay-commission-${c.id}`}>Pay now</Button>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">Paid ✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function UpsellPremium({ msg }) {  return (
    <div className="bg-white rounded-2xl border border-teal-100 p-10 text-center" data-testid="premium-upsell">
      <Crown className="w-12 h-12 text-[#F2635F] mx-auto" />
      <h3 className="font-display text-2xl font-bold mt-3">Corporate leads are Premium</h3>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">{msg || "Upgrade to Premium to receive corporate booking requests from companies in your area."}</p>
      <Link to="/pricing"><Button className="mt-5 rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white">Upgrade to Premium</Button></Link>
    </div>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>;
const Spinner = () => <div className="min-h-[40vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-teal-200 border-t-[#0FB5A6] animate-spin" /></div>;
const Empty = ({ label, to, cta }) => <div className="text-center py-10"><p className="text-slate-400">{label}</p><Link to={to}><Button variant="outline" className="mt-3 rounded-full font-bold border-teal-200">{cta}</Button></Link></div>;
const StatusPill = ({ status }) => {
  const map = { pending: "bg-teal-100 text-teal-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-600" };
  return <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${map[status]}`}>{status}</span>;
};
const TierBadge = ({ tier }) => {
  const map = { basic: "bg-slate-100 text-slate-600", pro: "bg-[#0FB5A6] text-white", premium: "bg-[#F2635F] text-slate-900" };
  return <span className={`px-4 py-2 rounded-full text-sm font-bold capitalize flex items-center gap-1 ${map[tier]}`}>{tier !== "basic" && <Crown className="w-4 h-4" />}{tier} plan</span>;
};
